import {
  Dialog,
  DialogActions,
  dialogClasses,
  DialogContent,
  DialogTitle,
  Grid,
  styled,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { LwButton, LwFormMultiSelect } from 'redesign';
import { FlexWorkerFlexPools } from '@types';
import { UseQueryResult } from 'react-query';
import { RenderUnsettledUI } from 'shared/components/render-unsettled-ui';
import { useTranslation } from 'react-i18next';
import { FlexWorkerFlexPoolsFormData } from '../choose-flex-worker-flex-pool-dialog.types';

type ChooseFlexWorkerFlexPoolsDialogProps = {
  isOpen: boolean;
  query: UseQueryResult<FlexWorkerFlexPools[]>;
  flexWorkerFullName: string;
  onClose: () => void;
  onSubmit: (formData: FlexWorkerFlexPoolsFormData) => void;
};

export const ChooseFlexWorkerFlexPoolDialog = ({
  isOpen,
  query,
  flexWorkerFullName,
  onClose,
  onSubmit,
}: ChooseFlexWorkerFlexPoolsDialogProps) => {
  const { t } = useTranslation(['all-platforms-common', 'web-common']);
  const methods = useForm<FlexWorkerFlexPoolsFormData>();

  useEffect(() => {
    if (!query.data || !isOpen) {
      return;
    }

    methods.reset({
      flexworkerFlexPoolsOptions: query.data.filter((pool) => pool.isMember),
    });
  }, [query.data, isOpen]);

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {t('flexpools.management.title', { ns: 'all-platforms-common' })} {flexWorkerFullName}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid item xs={12}>
              {query.status !== 'success' ? (
                <RenderUnsettledUI data={query} />
              ) : (
                <LwFormMultiSelect<FlexWorkerFlexPools, FlexWorkerFlexPoolsFormData>
                  name="flexworkerFlexPoolsOptions"
                  label={t('flexpools.name', { ns: 'all-platforms-common' })}
                  control={methods.control}
                  options={query.data}
                  getOptionKey={(option) => option.flexPoolId}
                  getOptionLabel={(option) => option.flexPoolName}
                  isOptionEqualToValue={(option, value) => option.flexPoolId === value.flexPoolId}
                />
              )}
            </Grid>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'space-around' }}>
            <LwButton color="secondary" onClick={onClose} autoFocus>
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
            <LwButton color="primary" type="submit">
              {t('buttons.confirm', { ns: 'web-common' })}
            </LwButton>
          </DialogActions>
        </form>
      </FormProvider>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)({
  [`& .${dialogClasses.paper}`]: {
    '&::-webkit-scrollbar': {
      width: 8,
    },

    '&::-webkit-scrollbar-track': {
      marginBlock: 16,
    },

    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.25)',
      borderRadius: 8,
    },
  },
});
